<script lang="ts" setup>
import {
  ClassroomEmailInfo,
  ClassroomEmailInfoRecipient,
} from "~/classes/models/classroom-emails/classroom-email-info.model";
import { ClassroomEmail } from "~/classes/models/classroom-emails/classroom-email.model";

const props = defineProps({
  recipients: {
    type: Array as PropType<ClassroomEmailInfoRecipient[]>,
    required: true,
  },
  classroomId: {
    type: String,
    required: true,
  },

  showSelectRecipients: {
    type: Boolean,
    default: true,
  },
  showAddManualRecipient: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "addRecipient",
  "removeRecipient",
  "update:modelValue",
]);

// watch for changes on recipients
watch(
  () => props.recipients,
  (newRecipients) => {}
);

const someRef = ref(false);

const removeRecipient = (email: string) => {
  var recipientIndex = props.recipients.findIndex((recipient) => {
    return recipient.email == email;
  });

  if (recipientIndex == -1) {
    return;
  }

  emit("removeRecipient", recipientIndex);
  someRef.value = !someRef.value;
};

const customRecipientEmail = ref("");
const addManualRecipient = () => {
  // Check if customRecipientEmail.value is a valid email
  if (useIsValidEmail(customRecipientEmail.value) != true) {
    useBaseToast("Please enter a valid email address.", "error");
    return;
  }

  const recipient = {
    label: "manually entered",
    email: customRecipientEmail.value,
    hasRead: false,
  };

  emit("addRecipient", recipient);

  customRecipientEmail.value = "";
};

const addRecipient = (recipient: ClassroomEmailInfoRecipient) => {
  emit("addRecipient", recipient);
  someRef.value = !someRef.value;
};

const removeRecipientFromIndex = (index: number) => {
  emit("removeRecipient", index);
  someRef.value = !someRef.value;
};
</script>

<template>
  <div class="flex flex-col">
    <div v-if="disabled != true && showSelectRecipients" class="mb-2 w-full">
      <ClassroomEmailsFormRecipientSelect
        :recipients="recipients"
        @add-recipient="addRecipient"
        @remove-recipient="removeRecipientFromIndex"
        @update:modelValue="emit('update:modelValue', $event)"
        :classroom-id="classroomId"
        :disabled="disabled"
        class="w-full"
      />
    </div>
    <div
      v-if="disabled != true && showAddManualRecipient"
      class="flex flex-row mb-2 w-[500px]"
    >
      <BaseTextInput
        v-model="customRecipientEmail"
        class="grow"
        placeholder="Enter additional recipient email"
        @keyup.enter="addManualRecipient"
      />
    </div>
    <div class="select-none" v-auto-animate>
      <span class="hidden"> {{ someRef }} </span>
      <div
        v-for="recipient in recipients"
        :key="recipient.email"
        class="mb-2 border-b last:border-b-0 pb-2"
      >
        <span
          class="flex flex-row w-full items-center py-1 justify-between hover:bg-light-default rounded pl-1"
        >
          <div class="flex flex-row items-center">
            <icon
              v-if="recipient.hasRead"
              name="mdi:check"
              class="text-success-defualt mr-2"
            />

            <span>
              <span class="capitalize">
                {{ recipient.label }}
              </span>
              - {{ recipient.email }}
            </span>
            <span v-if="recipient.hasRead">
              <span class="mx-1 ml-2"> | </span>
              <span class="italic text-body-2 text-success-default">
                Read at
                <span v-if="recipient.hasRead">
                  {{
                    useBasicDateTimeFormatter(recipient.lastViewedAtTimestamp)
                  }}
                </span>
              </span>
            </span>
          </div>
          <BaseDeleteButton
            v-if="disabled != true"
            color="danger"
            class="mr-1"
            @click="removeRecipient(recipient.email)"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
